/**
 * CVO Service
 * 
 * Handles all CVO-related API calls
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';

const cvoService = {
  /**
   * Get list of CVOs
   * @param {Object} params - Query parameters (page, limit, etc.)
   * @returns {Promise<Object>} List of CVOs
   */
  async getCVOs(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.cvo.list, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVOs' });
    }
  },

  /**
   * Get CVO by ID
   * @param {string} id - CVO ID
   * @returns {Promise<Object>} CVO details
   */
  async getCVO(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.cvo.detail(id));
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVO details' });
    }
  },

  /**
   * Create new CVO
   * @param {Object} payload - CVO payload
   * @returns {Promise<Object>} Created CVO
   */
  async createCVO(payload) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.cvo.create, payload);
      return {
        success: true,
        data: response.data,
        message: 'CVO created successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to create CVO' });
    }
  },

  /**
   * Update CVO (partial update)
   * @param {string} id - CVO ID
   * @param {Object} payload - CVO payload (partial fields allowed)
   * @returns {Promise<Object>} Updated CVO
   */
  async updateCVO(id, payload) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.cvo.update(id), payload);
      return {
        success: true,
        data: response.data,
        message: 'CVO updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update CVO' });
    }
  },

  /**
   * Delete CVO
   * @param {string} id - CVO ID
   * @returns {Promise<Object>}
   */
  async deleteCVO(id) {
    try {
      await apiClient.delete(API_ENDPOINTS.cvo.delete(id));
      return {
        success: true,
        message: 'CVO deleted successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to delete CVO' });
    }
  },
};

export default cvoService;

